import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useFocusTrap, useScrollLock } from "./focus";

interface OverlayBaseProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

function useEscape(open: boolean, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
}

/**
 * Dialog with focus trap, Esc to close, scroll lock. A centred card from 640px up; on phones a
 * bottom sheet that never reaches under the status bar and keeps its buttons above the home indicator.
 * <Modal open={open} onClose={close} title="Delete address?" footer={<GlowButton variant="danger">Delete</GlowButton>}>…</Modal>
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  size = "md",
}: OverlayBaseProps & { size?: "sm" | "md" | "lg" }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useFocusTrap(ref, open);
  useScrollLock(open);
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-4">
      <div className="absolute inset-0 animate-fade-in bg-scrim/55 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-desc` : undefined}
        tabIndex={-1}
        className={cn(
          "relative flex max-h-[calc(100dvh-var(--safe-top)-12px)] w-full animate-sheet-up flex-col rounded-t-[24px] border border-b-0 border-[var(--border-glow)] bg-elevated shadow-glow-3",
          "sm:max-h-[calc(100dvh-32px)] sm:animate-pop-in sm:rounded-card sm:border-b",
          { sm: "sm:max-w-sm", md: "sm:max-w-lg", lg: "sm:max-w-3xl" }[size],
          className,
        )}
      >
        <span className="mx-auto mt-2.5 h-1.5 w-12 shrink-0 rounded-full bg-fg/15 sm:hidden" aria-hidden />
        <div className="flex shrink-0 items-start justify-between gap-4 p-5 pb-0 sm:p-6 sm:pb-0">
          <div className="min-w-0">
            <h2 id={`${id}-title`} className="text-lg font-semibold">
              {title}
            </h2>
            {description && (
              <p id={`${id}-desc`} className="mt-1 text-sm text-ink-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 -mt-2 grid size-10 place-items-center rounded-full text-ink-muted hover:bg-fg/5 hover:text-ink"
            aria-label="Close dialog"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5 sm:p-6">{children}</div>
        {footer && (
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-line p-4 pb-[calc(16px+var(--safe-bottom))] *:w-full sm:flex-row sm:flex-wrap sm:justify-end sm:gap-3 sm:px-6 sm:pb-4 sm:*:w-auto">
            {footer}
          </div>
        )}
        {!footer && <div className="h-safe-b shrink-0 sm:hidden" aria-hidden />}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Edge panel. `side="bottom"` is the mobile sheet; left/right are drawers (cart, filters, nav).
 * <Drawer open side="right" title="Your cart" onClose={close}>…</Drawer>
 */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  side = "right",
}: OverlayBaseProps & { side?: "left" | "right" | "bottom" }) {
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useFocusTrap(ref, open);
  useScrollLock(open);
  useEscape(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 animate-fade-in bg-scrim/55 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-desc` : undefined}
        tabIndex={-1}
        className={cn(
          "absolute flex flex-col border-[var(--border-glow)] bg-elevated shadow-glow-3",
          side === "right" && "inset-y-0 right-0 w-full max-w-md animate-sheet-right border-l pt-safe-t",
          side === "left" && "inset-y-0 left-0 w-[88%] max-w-sm animate-sheet-left border-r pt-safe-t",
          side === "bottom" && "inset-x-0 bottom-0 max-h-[calc(100dvh-var(--safe-top)-12px)] animate-sheet-up rounded-t-[24px] border-t",
          className,
        )}
      >
        {side === "bottom" && <span className="mx-auto mt-3 h-1.5 w-12 rounded-full bg-fg/15" aria-hidden />}
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id={`${id}-title`} className="text-lg font-semibold">
              {title}
            </h2>
            {description && (
              <p id={`${id}-desc`} className="text-sm text-ink-muted">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mr-2 grid size-10 place-items-center rounded-full text-ink-muted hover:bg-fg/5 hover:text-ink"
            aria-label="Close panel"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-5">{children}</div>
        {footer ? (
          <div className="shrink-0 border-t border-line p-4 pb-[calc(16px+var(--safe-bottom))]">{footer}</div>
        ) : (
          <div className="h-safe-b shrink-0" aria-hidden />
        )}
      </div>
    </div>,
    document.body,
  );
}

/** Alias: a bottom Drawer (mobile action sheet). */
export function Sheet(props: OverlayBaseProps) {
  return <Drawer {...props} side="bottom" />;
}
